import AppShell from "../components/AppShell";
import { Link } from "react-router-dom";
import { BookOpen, Play, Compass, Search, Target, Briefcase, HardHat } from "lucide-react";
import { FUNDER_NAV } from "./FunderTerminal";
import { startFunderTour, resetFunderTour } from "../lib/tour";

const CHAPTERS = [
  { num: "01", icon: Target, title: "Confirm your mandate",
    body: "Whether you invest as an individual or institution, in businesses or projects — plus sectors, geography, ticket range and structures. Refine it any time from the Mandate tab." },
  { num: "02", icon: Search, title: "Read the Terminal",
    body: "Live, filtered deal flow sorted by compatibility with your mandate. Use the search and filter bar to drill into a sector, country, or readiness band." },
  { num: "03", icon: Briefcase, title: "Build your pipeline",
    body: "Save promising opportunities, move them through stages (Saved → VDR Opened → Replied → In Discussion → Closed). On any deal, click \"Accept NDA & Open VDR\" to express interest and request data-room access — the founder must approve." },
  { num: "04", icon: HardHat, title: "Explore projects too",
    body: "Beyond operating businesses, the Terminal surfaces scored projects — real estate units, energy plants, infrastructure and agri developments. Toggle between Businesses and Projects to match your focus." },
];

export default function FunderTutorial() {
  return (
    <AppShell nav={FUNDER_NAV}>
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Welcome aboard</p>
          <h1 className="text-3xl font-bold text-neutral-900 sm:text-4xl" style={{ fontFamily: "Manrope, sans-serif" }}>
            How to use the Clinq Terminal
          </h1>
          <p className="max-w-2xl text-sm text-neutral-600 sm:text-base">
            A short walkthrough of every section of your Funder Terminal — and what to do in your first week.
          </p>
        </div>

        {/* Interactive tour CTA */}
        <div className="mt-10 overflow-hidden rounded-3xl border border-[#0A8B85]/20 bg-gradient-to-br from-[#0A8B85]/[0.05] via-white to-[#F59E0B]/[0.05] p-6 sm:p-8" data-testid="tutorial-tour-card">
          <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#0A8B85] text-white shadow-[0_10px_25px_-10px_rgba(10,139,133,0.6)]">
                <Compass className="h-6 w-6" />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Interactive tour</p>
                <h3 className="mt-1 text-xl font-bold text-neutral-900 sm:text-2xl" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Take the guided 60-second tour
                </h3>
                <p className="mt-1.5 max-w-xl text-sm text-neutral-600">
                  Step-by-step pop-ups on your actual Terminal — search, filters, deal cards, and how to express interest in a deal. Click <b>Replay</b> any time.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to="/funder/terminal"
                data-testid="funder-tutorial-start-tour"
                onClick={() => { resetFunderTour(); }}
                className="inline-flex items-center gap-2 rounded-full bg-[#0A8B85] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-[#066667]"
              >
                <Play className="h-4 w-4 fill-current" /> Start tour
              </Link>
              <button
                type="button"
                data-testid="funder-tutorial-replay-here"
                onClick={() => startFunderTour()}
                className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-sm font-bold text-neutral-700 hover:border-[#0A8B85] hover:text-[#0A8B85]"
              >
                <Compass className="h-4 w-4" /> Replay here
              </button>
            </div>
          </div>
        </div>

        {/* Chapter cards */}
        <div className="mt-12">
          <div className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-[#0A8B85]" />
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0A8B85]">The 4-step playbook</p>
          </div>
          <h2 className="mt-2 text-2xl font-bold text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            What to do in your first week
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            {CHAPTERS.map(({ num, icon: Icon, title, body }) => (
              <div key={num} data-testid={`funder-tutorial-chapter-${num}`}
                className="group rounded-2xl border border-neutral-200 bg-white p-6 transition-shadow hover:shadow-md">
                <div className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0A8B85]/10 text-[#0A8B85] transition-colors group-hover:bg-[#0A8B85] group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-400">Chapter {num}</p>
                    <h3 className="mt-1 text-lg font-bold text-neutral-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                      {title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-600">{body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
